import google_protobuf_empty_pb from "google-protobuf/google/protobuf/empty_pb";
import type { Metadata } from "grpc-web";

import { EventEmitter } from "events";
import { InternalServiceClient } from "@chirpstack/chirpstack-api-grpc-web/api/internal_grpc_web_pb";
import type {
  UserTenantLink,
  OpenIdConnectLoginRequest,
  OAuth2LoginRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";
import { LoginRequest } from "@chirpstack/chirpstack-api-grpc-web/api/internal_pb";
import type { User } from "@chirpstack/chirpstack-api-grpc-web/api/user_pb";

import { HandleError, HandleLoginError } from "./helpers";
import { isMobileBrowser, isBraveBrowser, isIOSDevice, isPrivateBrowsingMode, isLowStorage, clearOldStorage } from "../utils/mobileUtils";

class SessionStore extends EventEmitter {
  client: InternalServiceClient;
  user?: User;
  tenants: UserTenantLink[];
  private sessionCheckInterval?: number;
  private isInitialized: boolean = false;

  constructor() {
    super();

    this.client = new InternalServiceClient("");
    this.tenants = [];

    // Initialize session management
    this.initializeSession();
    
    // Set up page visibility handling for mobile browsers
    this.setupPageVisibilityHandling();
    
    // Set up periodic session validation
    this.setupSessionValidation();
  }

  login = (email: string, password: string, callbackFunc: () => void) => {
    const req = new LoginRequest();
    req.setEmail(email);
    req.setPassword(password);
    this.client.login(req, {}, (err, resp) => {
      if (err !== null) {
        HandleLoginError(err);
        return;
      }

      this.setToken(resp.getJwt());
      this.fetchProfile(callbackFunc);
    });
  };

  openIdConnectLogin = (req: OpenIdConnectLoginRequest, callbackFunc: () => void) => {
    this.client.openIdConnectLogin(req, {}, (err, resp) => {
      if (err !== null) {
        HandleLoginError(err);
        return;
      }

      this.setToken(resp.getToken());
      this.fetchProfile(callbackFunc);
    });
  };

  oAuth2Login = (req: OAuth2LoginRequest, callbackFunc: () => void) => {
    this.client.oAuth2Login(req, {}, (err, resp) => {
      if (err !== null) {
        HandleLoginError(err);
        return;
      }

      this.setToken(resp.getToken());
      this.fetchProfile(callbackFunc);
    });
  };

  logout = (emit: boolean, callbackFunc: () => void) => {
    // Clear session validation interval
    if (this.sessionCheckInterval) {
      window.clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = undefined;
    }

    // Clear specific session-related items instead of clearing everything
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("tokenTimestamp");
      localStorage.removeItem("tokenBackup");
      localStorage.removeItem("tokenExtended");
      localStorage.removeItem("tokenExtendedTimestamp");
      localStorage.removeItem("tenantId");
      localStorage.removeItem("rowsPerPage");
    } catch (error) {
      console.error("Failed to clear localStorage items:", error);
    }
    
    try {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("tokenTimestamp");
      sessionStorage.removeItem("tokenBackup");
    } catch (error) {
      console.error("Failed to clear sessionStorage items:", error);
    }
    
    this.user = undefined;
    this.tenants = [];
    this.isInitialized = false;

    if (emit === true) {
      this.emit("change");
    }

    callbackFunc();
  };

  private initializeSession = async () => {
    if (this.isInitialized) return;
    
    // Clean up old storage on mobile devices
    if (isMobileBrowser()) {
      clearOldStorage();
      
      // Check if we're in private browsing mode
      const isPrivate = await isPrivateBrowsingMode();
      if (isPrivate) {
        console.log("Private browsing mode detected, using sessionStorage only");
      }
      
      // Check storage availability
      const lowStorage = await isLowStorage();
      if (lowStorage) {
        console.log("Low storage detected, clearing old data");
        clearOldStorage();
      }
    }
    
    // Try to restore session from storage
    const token = this.getToken();
    if (token) {
      this.fetchProfile(() => {
        this.isInitialized = true;
      });
    } else {
      this.isInitialized = true;
    }
  };

  private setupPageVisibilityHandling = () => {
    // Handle page visibility changes (important for mobile browsers)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && this.getToken()) {
          // Page became visible and we have a token, validate session
          this.validateSession();
        }
      });

      // Handle page focus/blur for additional mobile browser support
      window.addEventListener('focus', () => {
        if (this.getToken()) {
          this.validateSession();
        }
      });
    }
  };

  private setupSessionValidation = () => {
    // Validate session every 5 minutes to keep it alive
    this.sessionCheckInterval = window.setInterval(() => {
      if (this.getToken()) {
        this.validateSession();
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  private validateSession = () => {
    const token = this.getToken();
    if (!token) return;

    // Make a lightweight request to validate the session
    this.client.profile(new google_protobuf_empty_pb.Empty(), this.getMetadata(), (err, resp) => {
      if (err !== null) {
        // If token is invalid or expired, clear it
        if (err.code === 16 || err.code === 2) {
          console.log("Session expired, logging out");
          this.logout(false, () => {});
        }
      } else {
        // Session is valid, update user data
        this.user = resp.getUser();
        this.tenants = resp.getTenantsList();
        this.emit("change");
      }
    });
  };

  setToken = (s: string) => {
    const timestamp = Date.now().toString();
    const isMobile = isMobileBrowser();
    const isBrave = isBraveBrowser();
    const isIOS = isIOSDevice();
    
    // For mobile browsers, especially Brave on iOS, be more aggressive with storage
    if (isMobile && (isBrave || isIOS)) {
      // Store in both localStorage and sessionStorage for maximum persistence
      try {
        localStorage.setItem("token", s);
        localStorage.setItem("tokenTimestamp", timestamp);
        localStorage.setItem("tokenBackup", s); // Additional backup
      } catch (error) {
        console.error("Failed to store token in localStorage:", error);
      }
      
      try {
        sessionStorage.setItem("token", s);
        sessionStorage.setItem("tokenTimestamp", timestamp);
        sessionStorage.setItem("tokenBackup", s); // Additional backup
      } catch (error) {
        console.error("Failed to store token in sessionStorage:", error);
      }
      
      // For iOS Safari/Brave, also try to store in a more persistent way
      if (isIOS) {
        try {
          // Store with a longer expiration
          const extendedTimestamp = (Date.now() + 2 * 60 * 60 * 1000).toString(); // 2 hours from now
          localStorage.setItem("tokenExtended", s);
          localStorage.setItem("tokenExtendedTimestamp", extendedTimestamp);
        } catch (error) {
          console.error("Failed to store extended token:", error);
        }
      }
    } else {
      // Standard storage for desktop browsers
      try {
        localStorage.setItem("token", s);
        sessionStorage.setItem("token", s);
        localStorage.setItem("tokenTimestamp", timestamp);
        sessionStorage.setItem("tokenTimestamp", timestamp);
      } catch (error) {
        console.error("Failed to store token:", error);
        // Try alternative storage methods
        try {
          sessionStorage.setItem("token", s);
          sessionStorage.setItem("tokenTimestamp", timestamp);
        } catch (sessionError) {
          console.error("Failed to store token in sessionStorage:", sessionError);
        }
      }
    }
  };

  getToken = (): string => {
    const isMobile = isMobileBrowser();
    const isBrave = isBraveBrowser();
    const isIOS = isIOSDevice();
    
    // For mobile browsers, check multiple storage locations
    if (isMobile && (isBrave || isIOS)) {
      // Check localStorage first
      try {
        const token = localStorage.getItem("token");
        if (token && this.isTokenValid(token, "localStorage")) {
          return token;
        }
      } catch (error) {
        console.error("Failed to get token from localStorage:", error);
      }
      
      // Check localStorage backup
      try {
        const backupToken = localStorage.getItem("tokenBackup");
        if (backupToken && this.isTokenValid(backupToken, "localStorage")) {
          return backupToken;
        }
      } catch (error) {
        console.error("Failed to get backup token from localStorage:", error);
      }
      
      // For iOS, check extended storage
      if (isIOS) {
        try {
          const extendedToken = localStorage.getItem("tokenExtended");
          if (extendedToken && this.isTokenValid(extendedToken, "localStorage", true)) {
            return extendedToken;
          }
        } catch (error) {
          console.error("Failed to get extended token from localStorage:", error);
        }
      }
      
      // Check sessionStorage
      try {
        const sessionToken = sessionStorage.getItem("token");
        if (sessionToken && this.isTokenValid(sessionToken, "sessionStorage")) {
          return sessionToken;
        }
      } catch (error) {
        console.error("Failed to get token from sessionStorage:", error);
      }
      
      // Check sessionStorage backup
      try {
        const sessionBackupToken = sessionStorage.getItem("tokenBackup");
        if (sessionBackupToken && this.isTokenValid(sessionBackupToken, "sessionStorage")) {
          return sessionBackupToken;
        }
      } catch (error) {
        console.error("Failed to get backup token from sessionStorage:", error);
      }
    } else {
      // Standard token retrieval for desktop browsers
      try {
        const token = localStorage.getItem("token");
        if (token && this.isTokenValid(token, "localStorage")) {
          return token;
        }
      } catch (error) {
        console.error("Failed to get token from localStorage:", error);
      }

      // Fallback to sessionStorage
      try {
        const sessionToken = sessionStorage.getItem("token");
        if (sessionToken && this.isTokenValid(sessionToken, "sessionStorage")) {
          return sessionToken;
        }
      } catch (sessionError) {
        console.error("Failed to get token from sessionStorage:", sessionError);
      }
    }

    return "";
  };

  private isTokenValid = (token: string, storageType: "localStorage" | "sessionStorage", isExtended: boolean = false): boolean => {
    if (!token) return false;
    
    try {
      const timestampKey = isExtended ? "tokenExtendedTimestamp" : "tokenTimestamp";
      const timestamp = storageType === "localStorage" 
        ? localStorage.getItem(timestampKey)
        : sessionStorage.getItem(timestampKey);
        
      if (timestamp) {
        const tokenAge = Date.now() - parseInt(timestamp, 10);
        const maxAge = isExtended ? 2 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000; // 2 hours for extended, 24 hours for normal
        if (tokenAge > maxAge) {
          console.log(`${storageType} token too old, clearing`);
          this.logout(false, () => {});
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error(`Failed to validate token from ${storageType}:`, error);
      return false;
    }
  };

  getTenantId = (): string => {
    try {
      return localStorage.getItem("tenantId") || "";
    } catch (error) {
      console.error("Failed to get tenantId from localStorage:", error);
      return "";
    }
  };

  setTenantId = (id: string) => {
    console.log("tenantId set to", id);
    try {
      localStorage.setItem("tenantId", id);
    } catch (error) {
      console.error("Failed to store tenantId in localStorage:", error);
    }
    this.emit("tenant.change");
  };

  getRowsPerPage = (): number => {
    try {
      return parseInt(localStorage.getItem("rowsPerPage") || "10", 10);
    } catch (error) {
      console.error("Failed to get rowsPerPage from localStorage:", error);
      return 10;
    }
  };

  setRowsPerPage = (count: number) => {
    try {
      localStorage.setItem("rowsPerPage", count.toString());
    } catch (error) {
      console.error("Failed to store rowsPerPage in localStorage:", error);
    }
  };

  getMetadata = (): Metadata => {
    if (this.getToken() === "") {
      return {};
    }

    return {
      authorization: "Bearer " + this.getToken(),
    };
  };

  fetchProfile = (callbackFunc: () => void) => {
    const token = this.getToken();
    if (token === "") {
      callbackFunc();
      return;
    }

    this.client.profile(new google_protobuf_empty_pb.Empty(), this.getMetadata(), (err, resp) => {
      if (err !== null) {
        // If token is invalid or expired, clear it and don't show error
        if (err.code === 16 || err.code === 2) {
          console.log("Token invalid or expired during profile fetch");
          this.logout(false, callbackFunc);
          return;
        }
        HandleError(err);
        return;
      }

      this.user = resp.getUser();
      this.tenants = resp.getTenantsList();
      this.isInitialized = true;
      this.emit("change");

      callbackFunc();
    });
  };

  // Public method to check if session is ready
  isSessionReady = (): boolean => {
    return this.isInitialized;
  };

  // Public method to force session validation
  refreshSession = () => {
    if (this.getToken()) {
      this.validateSession();
    }
  };

  // Public method to attempt session recovery
  attemptSessionRecovery = () => {
    const token = this.getToken();
    if (token && !this.user) {
      console.log("Attempting session recovery...");
      this.fetchProfile(() => {
        console.log("Session recovery completed");
      });
    }
  };

  getUser = (): User | undefined => {
    return this.user;
  };

  isAdmin = (): boolean => {
    if (!this.user) {
      return false;
    }

    return this.user.getIsAdmin();
  };

  isTenantAdmin = (tenantId: string): boolean => {
    for (const t of this.tenants) {
      if (t.getTenantId() === tenantId) {
        return t.getIsAdmin();
      }
    }

    return false;
  };

  isTenantDeviceAdmin = (tenantId: string): boolean => {
    for (const t of this.tenants) {
      if (t.getTenantId() === tenantId) {
        return t.getIsAdmin() || t.getIsDeviceAdmin();
      }
    }

    return false;
  };

  isTenantGatewayAdmin = (tenantId: string): boolean => {
    for (const t of this.tenants) {
      return t.getIsAdmin() || t.getIsGatewayAdmin();
    }

    return false;
  };
}

const sessionStore = new SessionStore();
export default sessionStore;
